import React, { useState } from "react";
import { Box, Typography, Container, useMediaQuery, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Carousel from "react-material-ui-carousel";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import TouristDestinationCard from "../components/TouristDestinationCard";
import ExploreIcon from "@mui/icons-material/Explore";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { motion } from "framer-motion";

// Create a theme to pass into ThemeProvider
const theme = createTheme();

const ExploreAurangabadSection = () => {
  const [index, setIndex] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const cardsData = [
    {
      image: `${process.env.PUBLIC_URL}/images/kailashtemple.jpg`,
      title: "Kailash Temple",
      description:
        "The Kailasha or Kailashanatha temple is the largest of the rock-cut temples at the Ellora Caves.",
      link: "https://en.wikipedia.org/wiki/Kailasa_temple,_Ellora",
      loading: "lazy",
    },
    {
      image: `${process.env.PUBLIC_URL}/images/dulatabadfort.jpg`,
      title: "Dulatabad Fort",
      description:
        "It was the capital of the Yadavas, a secondary capital of the Ahmadnagar Sultanate.",
      link: "https://en.wikipedia.org/wiki/Daulatabad,_Maharashtra",
      loading: "lazy",
    },
    {
      image: `${process.env.PUBLIC_URL}/images/bibikamahal.jpg`,
      title: "Bibi Ka Makbara",
      description:
        "Known as the Mini Taj of the Deccan, commissioned by Aurangzeb's son in 1660.",
      link: "https://en.wikipedia.org/wiki/Bibi_Ka_Maqbara",
      loading: "lazy",
    },
    {
      image: `${process.env.PUBLIC_URL}/images/grishneshwartemple.jpg`,
      title: "Grishneshwar",
      description:
        "Grishneshwar Jyotirlinga is a Hindu temple of Shiva, one of the 12 Jyotirlingas.",
      link: "https://en.wikipedia.org/wiki/Grishneshwar_Temple",
      loading: "lazy",
    },
    {
      image: `${process.env.PUBLIC_URL}/images/Ajantacaves.jpg`,
      title: "Ajanta Caves",
      description:
        "The Ajanta Caves are 30 rock-cut Buddhist cave monuments dating from the second century BCE to about 480 CE.",
      link: "https://en.wikipedia.org/wiki/Ajanta_Caves",
      loading: "lazy",
    },
    {
      image: `${process.env.PUBLIC_URL}/images/PanChaki.jpg`,
      title: "Panchakki",
      description:
        "Panchakki, a water mill, is located in Aurangabad, Maharashtra, displays the scientific thought process put in medieval Indian architecture.",
      link: "https://en.wikipedia.org/wiki/Panchakki",
      loading: "lazy",
    },
  ];

  // Group cards based on screen size
  const cardsPerGroup = isSmallScreen ? 1 : isMediumScreen ? 2 : 3;
  
  // Group cards into sets based on screen size
  const groupedCards = [];
  for (let i = 0; i < cardsData.length; i += cardsPerGroup) {
    groupedCards.push(cardsData.slice(i, i + cardsPerGroup));
  }

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // Custom navigation buttons
  const CustomNavButton = ({ direction, onClick }) => {
    return (
      <motion.div
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Button
          onClick={onClick}
          sx={{
            minWidth: "48px",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.2)",
            color: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            left: direction === "prev" ? { xs: "5px", sm: "-24px" } : "auto",
            right: direction === "next" ? { xs: "5px", sm: "-24px" } : "auto",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.3)",
            },
            display: { xs: "none", sm: "flex" }
          }}
        >
          {direction === "prev" ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
        </Button>
      </motion.div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          py: 10,
          mt: 0,
          overflow: "hidden",
          background: "linear-gradient(135deg, #db352a 0%, #871e19 100%)",
        }}
      >
        {/* Background pattern */}
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "url('/images/world-map-dots.png')",
          backgroundSize: "cover",
          opacity: 0.05,
          zIndex: 0
        }} />
        
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: "relative",
            zIndex: 1
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center",
              mb: 6
            }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2,
                bgcolor: "rgba(255,255,255,0.15)",
                px: 3,
                py: 1,
                borderRadius: "50px"
              }}>
                <ExploreIcon sx={{ color: "white", mr: 1 }} />
                <Typography
                  variant="h3"
                  color="white"
                  align="center"
                  sx={{ 
                    fontWeight: "bold", 
                    fontSize: { xs: "1.8rem", md: "2.5rem" },
                  }}
                >
                  Explore Aurangabad
                </Typography>
              </Box>
              
              <Typography
                variant="body1"
                color="rgba(255,255,255,0.8)"
                align="center"
                sx={{ 
                  maxWidth: "700px",
                  mb: 2,
                  px: 2
                }}
              >
                Discover the ancient wonders and historical marvels of Aurangabad, 
                a city rich in culture and heritage.
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Box
              sx={{
                width: "100%",
                position: "relative",
                px: { xs: 1, sm: 4 },
                mt: 4,
              }}
            >
              <Carousel
                index={index}
                onChange={(event, value) => setIndex(value)}
                animation="slide"
                indicators={true}
                indicatorContainerProps={{
                  style: {
                    marginTop: "20px",
                    textAlign: "center",
                  }
                }}
                indicatorIconButtonProps={{
                  style: {
                    color: "rgba(255,255,255,0.4)",
                    padding: "5px",
                    marginRight: "4px",
                    marginLeft: "4px"
                  }
                }}
                activeIndicatorIconButtonProps={{
                  style: {
                    color: "white"
                  }
                }}
                autoPlay={false}
                interval={6000}
                stopAutoPlayOnHover={true}
                swipe={true}
                navButtonsAlwaysInvisible={true}
                sx={{ width: "100%" }}
                PrevIcon={<CustomNavButton direction="prev" />}
                NextIcon={<CustomNavButton direction="next" />}
                navButtonsWrapperProps={{
                  style: {
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }
                }}
              >
                {groupedCards.map((group, idx) => (
                  <Grid 
                    container 
                    spacing={3} 
                    key={idx}
                    sx={{ 
                      justifyContent: isSmallScreen ? "center" : "flex-start" 
                    }}
                  >
                    {group.map((card, cardIdx) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={cardIdx}
                        sx={{ 
                          display: "flex", 
                          justifyContent: "center"
                        }}
                      >
                        <motion.div
                          whileHover={{ y: -10 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          style={{ width: "100%", maxWidth: "350px" }}
                        >
                          <TouristDestinationCard
                            image={card.image}
                            title={card.title}
                            description={card.description}
                            link={card.link}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                ))}
              </Carousel>
              
              {/* Custom prev/next buttons for mobile */}
              <Box 
                sx={{ 
                  display: { xs: "flex", sm: "none" },
                  justifyContent: "center",
                  mt: 3,
                  gap: 2
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    onClick={() => setIndex(index === 0 ? groupedCards.length - 1 : index - 1)}
                    sx={{
                      minWidth: "40px",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                      }
                    }}
                  >
                    <KeyboardArrowLeftIcon />
                  </Button>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    onClick={() => setIndex(index === groupedCards.length - 1 ? 0 : index + 1)}
                    sx={{
                      minWidth: "40px",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                      }
                    }}
                  >
                    <KeyboardArrowRightIcon />
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ExploreAurangabadSection;